import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMyShifts, useShifts, useAvailableShifts, useMyRequests, useNotifications, useAvailability, useToast, useDepartments } from '../hooks/useData';
import { Btn, Card, Badge, Avatar, StatCard, Modal, FormRow, Input, EmptyState, PageHeader, Toast, Spinner, LoadingScreen } from '../components/UI';
import { fmtDate, fmtTime, getWeekStart, addDays, toISO, buildWeek, colorFor } from '../utils/dates';
import { supabase } from '../lib/supabase';
import '../components/UI.css';

// ── Employee Dashboard ────────────────────────────────────────────────────────
export function EmployeeDashboard() {
  const { profile, org } = useAuth();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const weekStart = toISO(getWeekStart(new Date()));
  const weekEnd   = toISO(addDays(getWeekStart(new Date()),6));
  const { shifts: myShifts, loading } = useMyShifts(weekStart, weekEnd);
  const { available } = useAvailableShifts();
  const { requests } = useMyRequests();
  const { unreadCount } = useNotifications();
  const { availability } = useAvailability();
  const availMap = Object.fromEntries(availability.map(a => [a.day_of_week, a]));
  const [postModal, setPostModal] = useState(null);
  const [reason, setReason] = useState('');
  const [postedIds, setPostedIds] = useState(new Set());
  const [claimedIds, setClaimedIds] = useState(new Set());
  const [warnShift, setWarnShift] = useState(null);

  useEffect(() => {
    if (!profile) return;
    supabase.from('available_shifts').select('shift_id').eq('posted_by', profile.id).eq('is_open', true)
      .then(({ data }) => setPostedIds(new Set((data || []).map(r => r.shift_id))));
  }, [profile]);

  useEffect(() => {
    const fromDB = requests.filter(r => r.status === 'pending').map(r => r.available_shift_id);
    if (fromDB.length) setClaimedIds(prev => new Set([...prev, ...fromDB]));
  }, [requests]);

  const pending = requests.filter(r => r.status === 'pending').length;
  const greetHour = new Date().getHours();
  const greeting = greetHour < 12 ? 'Good morning' : greetHour < 17 ? 'Good afternoon' : 'Good evening';

  async function handlePost(e) {
    e.preventDefault();
    if (!reason.trim()) return;
    await supabase.from('available_shifts').insert({ org_id: org.id, shift_id: postModal.id, posted_by: profile.id, reason }).select().single();
    const { data: others } = await supabase.from('profiles').select('id').eq('org_id', org.id).eq('role','employee').neq('id', profile.id);
    if (others?.length) await supabase.from('notifications').insert(others.map(u => ({ org_id: org.id, user_id: u.id, text: `New shift available: ${postModal.position} on ${postModal.shift_date}. Tap to claim.` })));
    setPostedIds(prev => new Set([...prev, postModal.id]));
    show('Shift posted for coverage!');
    setPostModal(null); setReason('');
  }

  async function submitClaim(availId) {
    setClaimedIds(prev => new Set([...prev, availId]));
    await supabase.from('shift_requests').insert({ org_id: org.id, available_shift_id: availId, requester_id: profile.id, status: 'pending' });
    const avail = available.find(a => a.id === availId);
    const { data: mgrs } = await supabase.from('profiles').select('id').eq('org_id', org.id).in('role',['manager','owner']);
    if (mgrs?.length) await supabase.from('notifications').insert(mgrs.map(m => ({ org_id: org.id, user_id: m.id, text: `${profile.name} requested to cover ${avail?.shift?.position} on ${avail?.shift?.shift_date}.` })));
    show('Request submitted!');
  }

  function handleClaim(a) {
    const existing = requests.find(r => r.available_shift_id === a.id && r.status === 'pending');
    if (existing || claimedIds.has(a.id)) return show('You already requested this shift.', 'error');
    if (a.shift?.shift_date) {
      const dow = new Date(a.shift.shift_date + 'T12:00:00').getDay();
      const avRow = availMap[dow];
      if (avRow && !avRow.is_available) { setWarnShift(a); return; }
    }
    submitClaim(a.id);
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title={`${greeting}, ${profile?.name?.split(' ')[0]} 👋`} subtitle={new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })} />
      <div className="stats-grid">
        <StatCard label="This week's shifts" value={myShifts.length} sub={`${myShifts.reduce((a,s)=>a+(+s.duration_hours),0)} hours`} />
        <StatCard label="Pending requests" value={pending} color={pending>0?'var(--amber)':undefined} sub="Awaiting approval" />
        <StatCard label="Open shifts" value={available.length} color="var(--accent)" sub="Available to claim" />
        <StatCard label="Unread alerts" value={unreadCount} color={unreadCount>0?'var(--red)':undefined} sub="Notifications" />
      </div>
      <div className="grid-2">
        <Card>
          <div className="card-header"><div className="card-title">Upcoming shifts</div><Btn size="sm" onClick={()=>navigate('/schedule')}>View all</Btn></div>
          {myShifts.length===0
            ? <EmptyState icon="📅" title="No shifts yet" message="Your schedule hasn't been published yet. Check back soon." />
            : myShifts.slice(0,4).map(s => (
              <div key={s.id} className="list-item">
                <div className="list-dot blue"></div>
                <div className="list-info">
                  <div className="list-title">{fmtDate(s.shift_date)}</div>
                  <div className="list-meta">{fmtTime(s.start_time)} – {fmtTime(s.end_time)} · {s.position}</div>
                </div>
                {postedIds.has(s.id) ? <Btn size="sm" disabled>Posted</Btn> : <Btn size="sm" onClick={() => setPostModal(s)}>Post</Btn>}
              </div>
            ))
          }
        </Card>
        <Card>
          <div className="card-header"><div className="card-title">Open shifts to claim</div><Btn size="sm" onClick={()=>navigate('/available')}>View all</Btn></div>
          {available.length===0
            ? <EmptyState icon="🔓" title="No open shifts" message="Check back later." />
            : available.slice(0,3).map(a => (
              <div key={a.id} className="list-item">
                <div className="list-dot green"></div>
                <div className="list-info">
                  <div className="list-title">{a.shift?.position} · {fmtDate(a.shift?.shift_date)}</div>
                  <div className="list-meta">"{a.reason}" · {a.posted_by_profile?.name}</div>
                </div>
                {claimedIds.has(a.id) ? <Btn size="sm" disabled>Request Submitted</Btn> : <Btn size="sm" variant="primary" onClick={() => handleClaim(a)}>Claim</Btn>}
              </div>
            ))
          }
        </Card>
      </div>

      <Modal isOpen={!!postModal} onClose={() => { setPostModal(null); setReason(''); }} title="Post shift for coverage">
        {postModal && (
          <form onSubmit={handlePost}>
            <div className="modal-info-box"><strong>{postModal.position}</strong><div style={{color:'var(--muted)',fontSize:13,marginTop:4}}>{fmtDate(postModal.shift_date)} · {fmtTime(postModal.start_time)} – {fmtTime(postModal.end_time)}</div></div>
            <FormRow label="Reason"><Input value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. medical appointment…" required /></FormRow>
            <div className="modal-actions"><Btn onClick={()=>setPostModal(null)}>Cancel</Btn><Btn type="submit" variant="primary">Post shift</Btn></div>
          </form>
        )}
      </Modal>

      <Modal isOpen={!!warnShift} onClose={() => setWarnShift(null)} title="Availability conflict">
        {warnShift && (
          <>
            <p style={{fontSize:13.5,color:'var(--muted)',marginBottom:20}}>
              You marked <strong>{['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(warnShift.shift?.shift_date+'T12:00:00').getDay()]}s</strong> as unavailable, but you can still request this shift if you'd like.
            </p>
            <div className="modal-actions">
              <Btn onClick={() => setWarnShift(null)}>Cancel</Btn>
              <Btn variant="primary" onClick={() => { submitClaim(warnShift.id); setWarnShift(null); }}>Request anyway</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ── My Schedule ───────────────────────────────────────────────────────────────
export function EmployeeSchedule() {
  const { profile, org } = useAuth();
  const { toast, show } = useToast();
  const weekStart = toISO(getWeekStart(new Date()));
  const weekEnd   = toISO(addDays(getWeekStart(new Date()), 6));
  const { shifts, loading, reload } = useShifts(weekStart, weekEnd);
  const { departments } = useDepartments();

  const [postModal, setPostModal]   = useState(null);
  const [swapModal, setSwapModal]   = useState(null);
  const [reason, setReason]         = useState('');
  const [swapping, setSwapping]     = useState(false);
  const [postedIds, setPostedIds]   = useState(new Set());
  const [myDeptIds, setMyDeptIds]   = useState(new Set());
  const [hasAny, setHasAny]         = useState(false);

  useEffect(() => {
    if (!profile) return;
    supabase.from('available_shifts').select('shift_id').eq('posted_by', profile.id).eq('is_open', true)
      .then(({ data }) => setPostedIds(new Set((data || []).map(r => r.shift_id))));
  }, [profile]);

  useEffect(() => {
    if (!profile) return;
    supabase.from('employee_departments').select('department_id, departments(is_any)').eq('employee_id', profile.id)
      .then(({ data }) => {
        setMyDeptIds(new Set((data || []).map(d => d.department_id)));
        setHasAny((data || []).some(d => d.departments?.is_any));
      });
  }, [profile]);

  const week = buildWeek(getWeekStart(new Date()));
  const todayISO = toISO(new Date());

  // Hide direct manager's shifts — employees shouldn't see their manager's schedule
  const visibleShifts = shifts.filter(s => s.employee_id !== profile?.reports_to);

  const shiftsByDate = {};
  visibleShifts.forEach(s => { (shiftsByDate[s.shift_date] = shiftsByDate[s.shift_date] || []).push(s); });

  const myShifts = shifts.filter(s => s.employee_id === profile?.id);

  function isEligible(shift) {
    if (hasAny) return true;
    if (shift.department_id) return myDeptIds.has(shift.department_id);
    return true;
  }

  function getMyShiftOnDate(date) {
    return shifts.find(s => s.employee_id === profile?.id && s.shift_date === date) || null;
  }

  async function handlePost(e) {
    e.preventDefault();
    if (!reason.trim()) return;
    await supabase.from('available_shifts').insert({ org_id: org.id, shift_id: postModal.id, posted_by: profile.id, reason });
    const { data: others } = await supabase.from('profiles').select('id').eq('org_id', org.id).eq('role', 'employee').neq('id', profile.id);
    if (others?.length) await supabase.from('notifications').insert(others.map(u => ({ org_id: org.id, user_id: u.id, text: `New shift available: ${postModal.position} on ${postModal.shift_date}.` })));
    setPostedIds(prev => new Set([...prev, postModal.id]));
    show('Shift posted!'); setPostModal(null); setReason(''); reload();
  }

  async function handleSwapRequest() {
    if (!swapModal) return;
    setSwapping(true);
    try {
      const { data: existing } = await supabase.from('available_shifts').select('id').eq('shift_id', swapModal.id).eq('is_open', true).maybeSingle();
      let availShiftId;
      if (existing) {
        availShiftId = existing.id;
      } else {
        const { data: posted } = await supabase.from('available_shifts').insert({
          org_id: org.id, shift_id: swapModal.id, posted_by: swapModal.employee_id,
          reason: `${profile.name} has requested a swap for this shift.`,
        }).select().single();
        availShiftId = posted.id;
      }
      const { data: existingReq } = await supabase.from('shift_requests').select('id')
        .eq('available_shift_id', availShiftId).eq('requester_id', profile.id).maybeSingle();
      if (existingReq) { show('You already have a pending swap request for this shift.', 'error'); return; }

      await supabase.from('shift_requests').insert({
        org_id: org.id, available_shift_id: availShiftId, requester_id: profile.id, status: 'pending',
      });
      const { data: mgrs } = await supabase.from('profiles').select('id').eq('org_id', org.id).in('role', ['manager','owner']);
      const targets = [...new Set([swapModal.employee_id, ...(mgrs||[]).map(m=>m.id)])].filter(id => id !== profile.id);
      if (targets.length) await supabase.from('notifications').insert(targets.map(uid => ({
        org_id: org.id, user_id: uid,
        text: `${profile.name} requested to swap the ${swapModal.position} shift on ${swapModal.shift_date}.`,
      })));
      show('Swap request sent!'); setSwapModal(null); reload();
    } catch(err) { show(err.message || 'Failed to send request.', 'error'); }
    finally { setSwapping(false); }
  }

  async function handleTradeRequest(myConflictShift) {
    if (!swapModal || !myConflictShift) return;
    setSwapping(true);
    try {
      const { data: existing } = await supabase.from('available_shifts').select('id').eq('shift_id', swapModal.id).eq('is_open', true).maybeSingle();
      let availShiftId;
      if (existing) {
        availShiftId = existing.id;
      } else {
        const { data: posted } = await supabase.from('available_shifts').insert({
          org_id: org.id, shift_id: swapModal.id, posted_by: swapModal.employee_id,
          reason: `${profile.name} proposed a trade: their ${myConflictShift.position} shift for this one.`,
        }).select().single();
        availShiftId = posted.id;
      }
      const { data: existingReq } = await supabase.from('shift_requests').select('id')
        .eq('available_shift_id', availShiftId).eq('requester_id', profile.id).maybeSingle();
      if (existingReq) { show('You already have a pending request for this shift.', 'error'); return; }

      await supabase.from('shift_requests').insert({
        org_id: org.id, available_shift_id: availShiftId, requester_id: profile.id,
        status: 'pending', offered_shift_id: myConflictShift.id,
      });
      const { data: mgrs } = await supabase.from('profiles').select('id').eq('org_id', org.id).in('role', ['manager','owner']);
      const targets = [...new Set([swapModal.employee_id, ...(mgrs||[]).map(m=>m.id)])].filter(id => id !== profile.id);
      if (targets.length) await supabase.from('notifications').insert(targets.map(uid => ({
        org_id: org.id, user_id: uid,
        text: `${profile.name} proposed a shift trade on ${swapModal.shift_date}.`,
      })));
      show('Trade request sent!'); setSwapModal(null); reload();
    } catch(err) { show(err.message || 'Failed to send trade request.', 'error'); }
    finally { setSwapping(false); }
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Schedule" subtitle={`Week of ${weekStart}`} />

      {/* Full org schedule grid */}
      <Card style={{ marginBottom:16, padding:'18px 16px' }}>
        <div className="schedule-scroll">
          <div className="week-grid">
            {week.map(({key, date, num}) => {
              const dayShifts = shiftsByDate[date] || [];
              return (
                <div key={date} className="day-col" style={{ cursor:'default' }}>
                  <div className="day-header">{key}</div>
                  <div className={`day-date ${date===todayISO?'today':''}`}>{num}</div>
                  {dayShifts.length === 0
                    ? <div className="day-off">No shifts</div>
                    : dayShifts.map(s => {
                        const isMe = s.employee_id === profile?.id;
                        const dept = departments.find(d => d.id === s.department_id && !d.is_any);
                        return (
                          <div key={s.id}
                            className={`shift-block ${colorFor(s.employee_id)}`}
                            style={{ ...(dept ? { borderLeft:`3px solid ${dept.color}` } : {}), cursor: isMe && postedIds.has(s.id) ? 'default' : 'pointer' }}
                            onClick={() => { if (isMe) { if (!postedIds.has(s.id)) setPostModal(s); } else setSwapModal(s); }}
                          >
                            <div className="sb-name">{isMe ? 'You' : s.employee?.name?.split(' ')[0]}</div>
                            <div className="sb-pos">{s.position}</div>
                            {dept && <div style={{ fontSize:10, opacity:0.75, marginTop:1 }}>{dept.name}</div>}
                            <div className="sb-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</div>
                          </div>
                        );
                      })
                  }
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* My shifts table */}
      <Card>
        <div className="card-header"><div className="card-title">My shifts this week ({myShifts.length})</div></div>
        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Date</th><th>Position</th><th>Dept</th><th>Start</th><th>End</th><th>Hours</th><th>Action</th></tr></thead>
            <tbody>{myShifts.map(s => (
              <tr key={s.id}>
                <td>{fmtDate(s.shift_date)}</td>
                <td><strong>{s.position}</strong></td>
                <td>{departments.find(d => d.id === s.department_id)?.name || s.department || '—'}</td>
                <td>{fmtTime(s.start_time)}</td><td>{fmtTime(s.end_time)}</td><td>{s.duration_hours}h</td>
                <td>{postedIds.has(s.id) ? <Btn size="sm" disabled>Posted</Btn> : <Btn size="sm" onClick={() => setPostModal(s)}>Post for coverage</Btn>}</td>
              </tr>
            ))}</tbody>
          </table>
          {myShifts.length === 0 && <div className="table-empty">You have no shifts scheduled this week.</div>}
        </div>
      </Card>

      {/* Post own shift for coverage */}
      <Modal isOpen={!!postModal} onClose={() => { setPostModal(null); setReason(''); }} title="Post shift for coverage">
        {postModal && <form onSubmit={handlePost}>
          <div className="modal-info-box"><strong>{postModal.position}</strong><div style={{color:'var(--muted)',fontSize:13,marginTop:4}}>{fmtDate(postModal.shift_date)} · {fmtTime(postModal.start_time)}–{fmtTime(postModal.end_time)}</div></div>
          <FormRow label="Reason"><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. medical appointment…" required /></FormRow>
          <div className="modal-actions"><Btn onClick={() => { setPostModal(null); setReason(''); }}>Cancel</Btn><Btn type="submit" variant="primary">Post shift</Btn></div>
        </form>}
      </Modal>

      {/* Swap request modal */}
      <Modal isOpen={!!swapModal} onClose={() => setSwapModal(null)} title="Shift details">
        {swapModal && (() => {
          const dept = departments.find(d => d.id === swapModal.department_id && !d.is_any);
          const deptName = dept?.name || swapModal.department || null;
          const eligible = isEligible(swapModal);
          const myConflictShift = getMyShiftOnDate(swapModal.shift_date);
          return (
            <>
              <div className="modal-info-box">
                <strong>{swapModal.employee?.name}</strong>
                <div style={{ color:'var(--muted)', fontSize:13, marginTop:4 }}>
                  {swapModal.position}{deptName ? ` · ${deptName}` : ''}<br />
                  {fmtDate(swapModal.shift_date)} · {fmtTime(swapModal.start_time)}–{fmtTime(swapModal.end_time)}
                </div>
              </div>
              {!eligible && (
                <div style={{ background:'var(--bg)', border:'1px solid var(--border-md)', borderRadius:'var(--radius-sm)', padding:'10px 14px', fontSize:13, color:'var(--muted)', marginBottom:12 }}>
                  You're not assigned to the <strong>{deptName}</strong> section. Ask your manager to add you before requesting a swap.
                </div>
              )}
              {eligible && myConflictShift && (
                <div style={{ background:'var(--bg)', border:'1px solid var(--border-md)', borderRadius:'var(--radius-sm)', padding:'10px 14px', fontSize:13, marginBottom:12 }}>
                  <div style={{ fontWeight:600, marginBottom:6 }}>You already have a shift that day — propose a trade instead?</div>
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12, color:'var(--muted)' }}>
                    <div>
                      <div style={{ fontWeight:600, color:'var(--text)', marginBottom:2 }}>Your shift</div>
                      <div>{myConflictShift.position}</div>
                      <div>{fmtTime(myConflictShift.start_time)}–{fmtTime(myConflictShift.end_time)}</div>
                    </div>
                    <div style={{ alignSelf:'center', fontSize:18, color:'var(--muted)' }}>⇄</div>
                    <div>
                      <div style={{ fontWeight:600, color:'var(--text)', marginBottom:2 }}>Their shift</div>
                      <div>{swapModal.position}</div>
                      <div>{fmtTime(swapModal.start_time)}–{fmtTime(swapModal.end_time)}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className="modal-actions">
                <Btn onClick={() => setSwapModal(null)}>Close</Btn>
                {eligible && !myConflictShift && (
                  <Btn variant="primary" onClick={handleSwapRequest} disabled={swapping}>
                    {swapping ? <Spinner /> : 'Request swap'}
                  </Btn>
                )}
                {eligible && myConflictShift && (
                  <Btn variant="primary" onClick={() => handleTradeRequest(myConflictShift)} disabled={swapping}>
                    {swapping ? <Spinner /> : 'Propose trade'}
                  </Btn>
                )}
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}

// ── Available Shifts ──────────────────────────────────────────────────────────
const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export function AvailableShifts() {
  const { profile, org } = useAuth();
  const { available, loading, reload } = useAvailableShifts();
  const { requests } = useMyRequests();
  const { availability } = useAvailability();
  const { toast, show } = useToast();
  const [claimedIds, setClaimedIds]   = useState(new Set());
  const [warnShift, setWarnShift]     = useState(null); // shift pending conflict confirm

  useEffect(() => {
    const fromDB = requests.filter(r => r.status === 'pending').map(r => r.available_shift_id);
    if (fromDB.length) setClaimedIds(prev => new Set([...prev, ...fromDB]));
  }, [requests]);

  // Build availability map: { day_of_week -> availability row }
  const availMap = {};
  availability.forEach(a => { availMap[a.day_of_week] = a; });

  async function submitClaim(a) {
    setClaimedIds(prev => new Set([...prev, a.id]));
    setWarnShift(null);
    await supabase.from('shift_requests').insert({ org_id: org.id, available_shift_id: a.id, requester_id: profile.id, status:'pending' });
    const { data: mgrs } = await supabase.from('profiles').select('id').eq('org_id',org.id).in('role',['manager','owner']);
    if (mgrs?.length) await supabase.from('notifications').insert(mgrs.map(m=>({ org_id:org.id, user_id:m.id, text:`${profile.name} requested to cover ${a.shift?.position} on ${a.shift?.shift_date}.` })));
    show('Request submitted!'); reload();
  }

  function handleClaim(a) {
    const already = requests.find(r => r.available_shift_id === a.id && r.status==='pending');
    if (already || claimedIds.has(a.id)) return show('You already requested this shift.','error');

    // Check availability conflict
    if (a.shift?.shift_date) {
      const dow = new Date(a.shift.shift_date + 'T12:00:00').getDay();
      const avRow = availMap[dow];
      if (avRow && !avRow.is_available) {
        setWarnShift(a); // show warning modal
        return;
      }
    }
    submitClaim(a);
  }

  if (loading) return <LoadingScreen />;
  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Available shifts" subtitle="Open shifts you can request to cover" />
      {available.length===0
        ? <EmptyState icon="🔓" title="No open shifts" message="All shifts are currently covered. Check back later!" />
        : available.map(a => {
          const already = requests.find(r => r.available_shift_id===a.id && r.status==='pending');
          const dow = a.shift?.shift_date ? new Date(a.shift.shift_date + 'T12:00:00').getDay() : null;
          const conflict = dow !== null && availMap[dow] && !availMap[dow].is_available;
          return (
            <div key={a.id} className="avail-card">
              <Avatar name={a.posted_by_profile?.name} color={a.posted_by_profile?.avatar_color} textColor={a.posted_by_profile?.avatar_text_color} size="lg" />
              <div className="avail-info">
                <div className="avail-title">{a.shift?.position} · {fmtDate(a.shift?.shift_date)}</div>
                <div className="avail-meta">{fmtTime(a.shift?.start_time)} – {fmtTime(a.shift?.end_time)} · {a.shift?.department} · Posted by {a.posted_by_profile?.name}</div>
                <div className="avail-reason">"{a.reason}"</div>
                {conflict && <div style={{ fontSize:12, color:'var(--amber)', fontWeight:500, marginTop:4 }}>⚠️ You marked {dow !== null ? DAY_NAMES[dow] : ''}s as unavailable</div>}
              </div>
              <div className="avail-actions">
                <Badge status="open" />
                {(already || claimedIds.has(a.id)) ? <Btn size="sm" disabled>Request Submitted</Btn> : <Btn size="sm" variant="primary" onClick={()=>handleClaim(a)}>Request to cover</Btn>}
              </div>
            </div>
          );
        })
      }

      {/* Availability conflict warning modal */}
      <Modal isOpen={!!warnShift} onClose={() => setWarnShift(null)} title="Availability conflict">
        {warnShift && (() => {
          const dow = new Date(warnShift.shift.shift_date + 'T12:00:00').getDay();
          return (
            <>
              <div style={{ background:'#fff8e6', border:'1px solid var(--amber)', borderRadius:'var(--radius-sm)', padding:'12px 14px', marginBottom:16, fontSize:13.5 }}>
                ⚠️ You've marked <strong>{DAY_NAMES[dow]}</strong> as unavailable in your availability settings. Your manager can still see this conflict.
              </div>
              <p style={{ fontSize:13.5, color:'var(--muted)', marginBottom:16 }}>Do you still want to request this shift?</p>
              <div style={{ fontSize:13, marginBottom:16 }}>
                <strong>{warnShift.shift?.position}</strong> · {fmtDate(warnShift.shift?.shift_date)}<br/>
                {fmtTime(warnShift.shift?.start_time)} – {fmtTime(warnShift.shift?.end_time)}
              </div>
              <div className="modal-actions">
                <Btn onClick={() => setWarnShift(null)}>Cancel</Btn>
                <Btn variant="primary" onClick={() => submitClaim(warnShift)}>Request anyway</Btn>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}

// ── My Requests ───────────────────────────────────────────────────────────────
export function MyRequests() {
  const { requests, loading } = useMyRequests();
  if (loading) return <LoadingScreen />;
  return (
    <div className="page">
      <PageHeader title="My requests" subtitle="Track your shift coverage requests" />
      <Card>
        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Position</th><th>Date</th><th>Time</th><th>Dept</th><th>Submitted</th><th>Status</th></tr></thead>
            <tbody>{requests.map(r => {
              const shift = r.available_shift?.shift;
              return (
                <tr key={r.id}><td><strong>{shift?.position}</strong></td><td>{fmtDate(shift?.shift_date)}</td><td>{fmtTime(shift?.start_time)}–{fmtTime(shift?.end_time)}</td><td>{shift?.department}</td><td style={{color:'var(--muted)',fontSize:12}}>{new Date(r.created_at).toLocaleDateString()}</td><td><Badge status={r.status} /></td></tr>
              );
            })}</tbody>
          </table>
          {requests.length===0 && <div className="table-empty">No requests yet. Claim an available shift to get started.</div>}
        </div>
      </Card>
    </div>
  );
}

// ── My Availability ───────────────────────────────────────────────────────────
const AVAIL_DAYS = [
  { label: 'Monday',    dow: 1 },
  { label: 'Tuesday',   dow: 2 },
  { label: 'Wednesday', dow: 3 },
  { label: 'Thursday',  dow: 4 },
  { label: 'Friday',    dow: 5 },
  { label: 'Saturday',  dow: 6 },
  { label: 'Sunday',    dow: 0 },
];

export function EmployeeAvailability() {
  const { toast, show } = useToast();
  const { availability, loading, saveAvailability } = useAvailability();
  const [rows, setRows] = useState(
    AVAIL_DAYS.map(d => ({ day_of_week: d.dow, is_available: false, start_time: '09:00', end_time: '17:00' }))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    const map = {};
    availability.forEach(a => { map[a.day_of_week] = a; });
    setRows(AVAIL_DAYS.map(({ dow }) => ({
      day_of_week:  dow,
      is_available: map[dow]?.is_available ?? false,
      start_time:   (map[dow]?.start_time || '09:00:00').slice(0, 5),
      end_time:     (map[dow]?.end_time   || '17:00:00').slice(0, 5),
    })));
  }, [availability, loading]);

  function update(dow, field, value) {
    setRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, [field]: value } : r));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await saveAvailability(rows);
      show('Availability saved!');
    } catch (err) {
      show(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="My availability" subtitle="Set which days and times you're available to work" />
      <Card>
        <form onSubmit={handleSave}>
          <div style={{ display:'flex', flexDirection:'column' }}>
            {AVAIL_DAYS.map(({ label, dow }) => {
              const row = rows.find(r => r.day_of_week === dow) || { is_available: false, start_time: '09:00', end_time: '17:00' };
              return (
                <div key={dow} style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, width:130, cursor:'pointer', flexShrink:0 }}>
                    <input
                      type="checkbox"
                      checked={row.is_available}
                      onChange={e => update(dow, 'is_available', e.target.checked)}
                      style={{ width:16, height:16, accentColor:'var(--accent)', cursor:'pointer' }}
                    />
                    <span style={{ fontSize:14, fontWeight: row.is_available ? 600 : 400, color: row.is_available ? 'var(--text)' : 'var(--muted)' }}>
                      {label}
                    </span>
                  </label>
                  {row.is_available ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Input type="time" step="900" value={row.start_time} onChange={e => update(dow, 'start_time', e.target.value)} style={{ width:120 }} />
                      <span style={{ color:'var(--muted)', fontSize:14 }}>–</span>
                      <Input type="time" step="900" value={row.end_time}   onChange={e => update(dow, 'end_time',   e.target.value)} style={{ width:120 }} />
                    </div>
                  ) : (
                    <span style={{ fontSize:13, color:'var(--muted)' }}>Not available</span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:20, display:'flex', justifyContent:'flex-end' }}>
            <Btn type="submit" variant="primary" disabled={saving}>{saving ? <Spinner /> : 'Save availability'}</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ── Notifications ─────────────────────────────────────────────────────────────
export function EmpNotifications() {
  const { notifications, unreadCount, markAllRead } = useNotifications();
  return (
    <div className="page">
      <PageHeader title="Notifications" subtitle={`${unreadCount} unread`}
        action={unreadCount>0 && <Btn size="sm" onClick={markAllRead}>Mark all read</Btn>}
      />
      <Card>
        {notifications.length===0
          ? <EmptyState icon="🔔" title="All caught up" message="No notifications yet." />
          : notifications.map(n => (
            <div key={n.id} className="notif-item">
              <div className={`notif-dot ${n.read?'read':''}`}></div>
              <div style={{flex:1}}>
                <div className={`notif-text ${!n.read?'unread':''}`}>{n.text}</div>
                <div className="notif-time">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))
        }
      </Card>
    </div>
  );
}
